import { useMemo, useState, type CSSProperties, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { DealCategory, DealStage, type DealSummaryDto } from '@bhops/shared';
import { dealsApi } from '@/api/deals';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { canEditSales } from '@/permissions';
import { Button } from '@/components/Form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { MaskedPrice } from '@/components/MaskedPrice';
import { DealCreateModal } from './deals/DealCreateModal';
import {
  DealTransitionModal,
  canTransition,
  isStageTransitionRoleAllowed,
} from './deals/DealTransitionModal';
import { columnStyle } from './deals/kanban-format';

// Phase 5 Deal Kanban — plan §05 §3 (4-quadrant becomes 6-column
// after the BR-DEAL-01 6-stage canonicalization). Each column is a
// stage; cards show humanId, customer, salesRep, robot count,
// expectedRevenue, and a category badge (NEW / RENEWAL / EXPANSION).

const STAGES: DealStage[] = [
  DealStage.LEAD,
  DealStage.QUALIFIED,
  DealStage.PROPOSAL,
  DealStage.NEGOTIATION,
  DealStage.ACTIVE,
  DealStage.CHURNED,
];

const STAGE_LABEL: Record<DealStage, string> = {
  LEAD: '리드',
  QUALIFIED: '검증',
  PROPOSAL: '제안',
  NEGOTIATION: '협상',
  ACTIVE: '계약 체결',
  CHURNED: '이탈',
};

const CATEGORY_LABEL: Record<DealCategory, string> = {
  NEW: '신규',
  RENEWAL: '재계약',
  EXPANSION: '증설',
};

const CATEGORY_COLOR: Record<DealCategory, string> = {
  NEW: 'var(--report-new)',
  RENEWAL: 'var(--robot-operating)',
  EXPANSION: 'var(--report-assigned)',
};

interface Props {
  /**
   * Plan v0.3.0 §F-3 — embed mode for CustomerMasterPage's sub-tab.
   * Hides the page h1 (parent shows the customer's header). Filter
   * search bar still renders.
   */
  embedded?: boolean;
  /** §F-3 — when set, filter the kanban to this customer's deals. */
  customerId?: string;
}

export function DealsKanbanPage({ embedded = false, customerId }: Props = {}) {
  const userRole = useAuthStore((s) => s.user?.role);
  // 영업 도메인(딜 파이프라인) — canEditSales = ADMIN/SALES_MANAGER/MANAGER.
  // 기존엔 SALES_MANAGER 가 빠져 영업팀 매니저가 딜 생성/이동 버튼을 못 봤다.
  // (백엔드 CASL 도 SALES_MANAGER 에 Deal create/update 부여하도록 동반 수정.)
  const canCreate = !!userRole && canEditSales(userRole);
  const canTransitionDeals = canCreate; // 동일 게이트
  // data-lifecycle §3 — 삭제됨 영업기회 노출/복구는 영업 편집 권한자에게만
  // (백엔드 CASL delete Deal 와 일치).
  const canRestore = canCreate;
  const qc = useQueryClient();
  const [filter, setFilter] = useState({ search: '' });
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const restore = useMutation({
    mutationFn: (id: string) => dealsApi.restore(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deals'] }),
  });

  // data-lifecycle §3/D3 — 셀프 완전삭제(되돌릴 수 없음, 2단계 확인).
  const [hardDeleteTarget, setHardDeleteTarget] = useState<DealSummaryDto | null>(null);
  const hardDelete = useMutation({
    mutationFn: (id: string) => dealsApi.hardDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      setHardDeleteTarget(null);
    },
  });

  // DnD state — using native HTML5 drag (no @dnd-kit dep). The
  // dragged Deal is held in `draggingDeal`; on drop in a column,
  // we open the DealTransitionModal pre-filled with `to=<column
  // stage>`.
  const [draggingDeal, setDraggingDeal] = useState<DealSummaryDto | null>(null);
  const [dragOverStage, setDragOverStage] = useState<DealStage | null>(null);
  const [transitionTarget, setTransitionTarget] = useState<{
    deal: DealSummaryDto;
    to: DealStage;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['deals', 'all', filter, includeDeleted, customerId],
    queryFn: () =>
      dealsApi.list({
        search: filter.search || undefined,
        ...(customerId ? { customerId } : {}),
        ...(includeDeleted ? { includeDeleted: true } : {}),
        pageSize: 200,
      }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  // Bucket deals by stage. Empty array per stage is the
  // "render-without-null-guards" contract.
  const byStage = useMemo(() => {
    const buckets: Record<DealStage, DealSummaryDto[]> = {
      LEAD: [],
      QUALIFIED: [],
      PROPOSAL: [],
      NEGOTIATION: [],
      ACTIVE: [],
      CHURNED: [],
    };
    for (const d of data?.items ?? []) buckets[d.stage].push(d);
    return buckets;
  }, [data?.items]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: embedded ? 8 : 16,
        height: '100%',
      }}
    >
      {/* §F-3 — page header hidden in embedded mode (parent shows customer). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {!embedded && (
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            영업 파이프라인
          </h1>
        )}
        <input
          placeholder="고객사 이름이나 영업기회 번호로 검색"
          value={filter.search}
          onChange={(e) => setFilter({ search: e.target.value })}
          style={{
            marginLeft: 12,
            width: 260,
            height: 32,
            padding: '0 10px',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-input)',
            background: 'var(--bg-surface)',
            fontSize: 13,
          }}
        />
        {/* data-lifecycle §3 — 삭제됨 영업기회 포함(영업 편집 권한자 전용). 복구용. */}
        {canRestore && (
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              padding: '4px 10px',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-btn)',
              background: includeDeleted ? 'var(--bg-accent-subtle)' : 'var(--bg-surface)',
              color: includeDeleted ? 'var(--fg-brand)' : 'var(--fg-default)',
              cursor: 'pointer',
              fontWeight: includeDeleted ? 600 : 400,
            }}
            title="삭제된 영업기회도 함께 보여 줍니다. 여기서 복구할 수 있습니다."
          >
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              style={{ margin: 0 }}
            />
            삭제됨 포함
          </label>
        )}
        <div className="kuun-caption" style={{ marginLeft: 'auto', color: 'var(--fg-muted)' }}>
          총 {(data?.total ?? 0).toLocaleString()} 건
        </div>
        {canCreate && (
          <Button type="button" onClick={() => setCreateOpen(true)}>
            {/* PDF p.13: 운영 용어로 "Deal" → "고객사" 통일 */}+ 신규 고객사
          </Button>
        )}
      </div>

      <DealCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        // §F-3 follow-up — pre-select embedded customer.
        initialCustomerId={customerId}
      />

      {transitionTarget && (
        <DealTransitionModal
          open={true}
          onClose={() => setTransitionTarget(null)}
          deal={transitionTarget.deal}
          to={transitionTarget.to}
        />
      )}

      {error ? (
        <div style={errorBox}>
          <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(error)}</span>
        </div>
      ) : isLoading && !data ? (
        <div style={empty}>
          <span className="kuun-caption">불러오는 중…</span>
        </div>
      ) : (
        <div style={kanbanGrid}>
          {STAGES.map((stage) => {
            const isOver = dragOverStage === stage;
            // round3 #8e — ACTIVE(계약 체결) 드롭은 ADMIN 전용(서버 규칙 미러).
            // role 미허용 타깃은 드롭 금지 표시 + 드롭 비활성(422 방지).
            const roleOk = isStageTransitionRoleAllowed(stage, userRole);
            const dropAllowed =
              draggingDeal !== null &&
              draggingDeal.stage !== stage &&
              canTransition(draggingDeal.stage, stage) &&
              roleOk;
            const dropForbidden =
              draggingDeal !== null &&
              draggingDeal.stage !== stage &&
              (!canTransition(draggingDeal.stage, stage) || !roleOk);

            return (
              <div
                key={stage}
                style={columnStyle(isOver, dropAllowed, dropForbidden)}
                onDragOver={(e: DragEvent<HTMLDivElement>) => {
                  if (!canTransitionDeals || !draggingDeal) return;
                  if (draggingDeal.stage === stage) return;
                  // round3 #8e — role 미허용 타깃(예: 비-ADMIN 의 ACTIVE)은 드롭을
                  // 활성화하지 않는다(preventDefault 생략 → drop 불가).
                  if (!roleOk) return;
                  // preventDefault enables the drop event.
                  e.preventDefault();
                  setDragOverStage(stage);
                }}
                onDragLeave={() => {
                  if (dragOverStage === stage) setDragOverStage(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverStage(null);
                  if (!draggingDeal) return;
                  // Open the transition modal — server SM is the
                  // gate even for "valid"-looking drops, and the
                  // modal collects the reason / churnReason.
                  setTransitionTarget({ deal: draggingDeal, to: stage });
                  setDraggingDeal(null);
                }}
              >
                <div style={columnHeader}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{STAGE_LABEL[stage]}</span>
                  <span
                    className="kuun-caption"
                    style={{
                      marginLeft: 'auto',
                      color: 'var(--fg-muted)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {byStage[stage].length}
                  </span>
                </div>
                <div style={columnBody}>
                  {byStage[stage].length === 0 ? (
                    <div style={emptyCell} className="kuun-caption">
                      -
                    </div>
                  ) : (
                    byStage[stage].map((deal) => (
                      <DealCard
                        key={deal.id}
                        deal={deal}
                        // 삭제됨 카드는 드래그(전이) 불가 — 복구 먼저.
                        draggable={canTransitionDeals && !deal.deletedAt}
                        onDragStart={() => setDraggingDeal(deal)}
                        onDragEnd={() => {
                          setDraggingDeal(null);
                          setDragOverStage(null);
                        }}
                        canRestore={canRestore && !!deal.deletedAt}
                        onRestore={() => restore.mutate(deal.id)}
                        onHardDelete={() => setHardDeleteTarget(deal)}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* data-lifecycle §3/D3 — 완전삭제 2단계 확인(되돌릴 수 없음). */}
      <ConfirmDialog
        open={!!hardDeleteTarget}
        title="영업기회 완전 삭제"
        message={`${hardDeleteTarget?.humanId ?? ''} 영업기회를 완전히 삭제합니다. 되돌릴 수 없습니다. 계약이나 진행 중인 생산 할당이 있으면 삭제되지 않습니다.`}
        confirmLabel="완전 삭제"
        danger
        busy={hardDelete.isPending}
        onConfirm={() => {
          if (hardDeleteTarget) hardDelete.mutate(hardDeleteTarget.id);
        }}
        onCancel={() => setHardDeleteTarget(null)}
      />
    </div>
  );
}

function DealCard({
  deal,
  draggable = false,
  onDragStart,
  onDragEnd,
  canRestore = false,
  onRestore,
  onHardDelete,
}: {
  deal: DealSummaryDto;
  draggable?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  // data-lifecycle §3 — 삭제됨 카드 복구 + 완전삭제.
  canRestore?: boolean;
  onRestore?: () => void;
  onHardDelete?: () => void;
}) {
  const inner = (
    <>
      <div style={cardTopRow}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-muted)' }}>
          {deal.humanId}
        </span>
        {deal.deletedAt && (
          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--severity-urgent)' }}>
            삭제됨
          </span>
        )}
        <span style={{ ...categoryBadge, background: CATEGORY_COLOR[deal.category] }}>
          {CATEGORY_LABEL[deal.category]}
        </span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, marginTop: 6 }}>{deal.customer.displayName}</div>
      {deal.store && (
        <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
          {[deal.store.name, deal.store.branchName].filter(Boolean).join(' · ')}
        </div>
      )}
      <div style={cardMeta}>
        <span>담당: {deal.salesRepCode}</span>
        {deal.expectedRobotCount != null && <span>로봇 {deal.expectedRobotCount}대</span>}
        {/*
          Plan §D-3 (PDF p.17 ②) — revenue masked for non-sales roles.
          MaskedPrice handles the redact treatment + suffix internally.
        */}
        <span style={{ marginLeft: 'auto' }}>
          <MaskedPrice value={deal.expectedRevenue} format="abbr" suffix="원" />
        </span>
      </div>
      {deal._count && (deal._count.activities > 0 || deal._count.contracts > 0) && (
        <div style={cardMeta}>
          {deal._count.activities > 0 && <span>활동 {deal._count.activities}</span>}
          {deal._count.contracts > 0 && <span>계약 {deal._count.contracts}</span>}
        </div>
      )}
    </>
  );

  // data-lifecycle §3 — 삭제됨 카드는 Link(상세 진입·드래그) 대신 흐린 정적
  // 카드 + 복구 버튼. 클릭이 상세로 새지 않도록 div 로 렌더.
  if (deal.deletedAt) {
    return (
      <div style={{ ...card, cursor: 'default', opacity: 0.55 }}>
        {inner}
        {canRestore && (
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button
              type="button"
              onClick={onRestore}
              style={{
                flex: 1,
                fontSize: 12,
                padding: '4px 8px',
                borderRadius: 'var(--r-btn)',
                border: '1px solid var(--fg-brand)',
                background: 'var(--bg-info-subtle)',
                color: 'var(--fg-brand)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              복구
            </button>
            {/* data-lifecycle §3/D3 — 셀프 완전삭제(되돌릴 수 없음). */}
            <button
              type="button"
              onClick={onHardDelete}
              title="이 영업기회를 완전히 삭제합니다. 되돌릴 수 없습니다."
              style={{
                flex: 1,
                fontSize: 12,
                padding: '4px 8px',
                borderRadius: 'var(--r-btn)',
                border: '1px solid var(--severity-urgent)',
                background: 'var(--bg-surface)',
                color: 'var(--severity-urgent)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              완전 삭제
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={`/deals/${deal.id}`}
      draggable={draggable}
      onDragStart={(e) => {
        // Required for Firefox to fire dragstart at all.
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', deal.id);
        onDragStart?.();
      }}
      onDragEnd={() => onDragEnd?.()}
      style={{ ...card, textDecoration: 'none', color: 'inherit' }}
    >
      {inner}
    </Link>
  );
}

// formatKRW + columnStyle live in `./deals/kanban-format` so the
// pure logic can be unit-tested without rendering this page.

const kanbanGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(6, minmax(220px, 1fr))',
  gap: 12,
  flex: 1,
  overflowX: 'auto',
};

// `column` base + `columnStyle()` live in `./deals/kanban-format`.

const columnHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  borderBottom: '1px solid var(--border-subtle)',
};

const columnBody: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 8,
  overflowY: 'auto',
  flex: 1,
};

const card: CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-card)',
  borderRadius: 'var(--r-card)',
  padding: 10,
  cursor: 'pointer',
  transition: 'box-shadow 0.1s',
};

const cardTopRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const cardMeta: CSSProperties = {
  display: 'flex',
  gap: 10,
  marginTop: 8,
  fontSize: 11,
  color: 'var(--fg-muted)',
  fontFamily: 'var(--font-mono)',
};

const categoryBadge: CSSProperties = {
  marginLeft: 'auto',
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--fg-inverse)',
  padding: '2px 6px',
  borderRadius: 'var(--r-pill)',
};

const empty: CSSProperties = { padding: 40, textAlign: 'center' };
const emptyCell: CSSProperties = {
  padding: 16,
  textAlign: 'center',
  color: 'var(--fg-subtle)',
};
const errorBox: CSSProperties = {
  padding: 16,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
};
